<script lang="ts" setup>
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useGptAdManager } from '../composables/useGptAdManager'
import { createDebugger } from '@/utils/debug'
import { pushAxonEvent } from '@/utils/axonTrack'

const props = defineProps<{
  adSlotIndex: number
  adInstanceId: string
  adOrder: number
  chapterId: string
}>()

const debug = createDebugger('ad')

const {
  getAdSlotForIndex,
  registerPreloadItem,
  waitForPreloadTurn,
  releaseNextPreloadBatch,
  displayAd,
  destroyAd,
  AD_ENABLED,
  PRELOAD_BATCH_SIZE,
} = useGptAdManager()
const containerRef = ref<HTMLElement>()

const globalPreloadOrder = registerPreloadItem()
const preloadBatch = Math.ceil(globalPreloadOrder / PRELOAD_BATCH_SIZE)
const divId = `gpt-storyella-${props.adSlotIndex}-${props.adInstanceId.replace(/[^a-zA-Z0-9_-]/g, '-')}`
const slotConfig = getAdSlotForIndex(props.adSlotIndex)
const unitPath = slotConfig.unitPath
const slotName = slotConfig.name
const logContext = [
  `chapterId=${props.chapterId}`,
  `adOrder=${props.adOrder}`,
  `globalOrder=${globalPreloadOrder}`,
  `batch=${preloadBatch}`,
  `slot=${slotName}`,
  `unit=${unitPath}`,
  `div=${divId}`,
]
let loadObserver: IntersectionObserver | null = null
let reportObserver: IntersectionObserver | null = null
let reported = false
let filled = false
let visible = false
let shown = false
let disposed = false
let preloadFinished = false

function maybeReleaseNextBatchForEmptyBoundary() {
  if (!preloadFinished || filled || !visible)
    return
  if (globalPreloadOrder % PRELOAD_BATCH_SIZE !== 0)
    return
  debug('批次边界广告无填充，释放下一批预加载 %s', logContext.join(' '))
  releaseNextPreloadBatch(globalPreloadOrder)
}

function maybeShowAd() {
  if (!filled || !visible)
    return
  showAd()
}

function reportVisible() {
  if (reported)
    return
  reported = true
  pushAxonEvent('purchase', {
    currency: 'USD',
    value: 0.001,
    transaction_id: `ad_gpt_${props.adSlotIndex}_${Date.now()}`,
    items: [{
      item_id: `ad_slot_gpt_${props.adSlotIndex}`,
      item_name: `GPT_Slot_${props.adSlotIndex}`,
      price: 0,
      quantity: 1,
    }],
  }, { is_taboola_ad_purchase: true })
  debug('可见性上报 %s', logContext.join(' '))
}

function showAd() {
  if (shown)
    return
  shown = true
  const el = containerRef.value
  if (!el)
    return
  el.style.visibility = 'visible'
  el.style.maxHeight = 'none'
  el.style.minHeight = '250px'
  el.style.overflow = 'visible'
  debug('广告展开展示 %s', logContext.join(' '))
  releaseNextPreloadBatch(globalPreloadOrder)
  reportObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting)
        continue
      reportObserver?.disconnect()
      reportObserver = null
      reportVisible()
    }
  }, { threshold: 0.5 })
  reportObserver.observe(el)
}

onMounted(async () => {
  if (!AD_ENABLED)
    return
  const el = containerRef.value
  if (!el)
    return
  await nextTick()

  loadObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting)
        continue
      loadObserver?.disconnect()
      loadObserver = null
      visible = true
      debug('广告位进入可见范围 filled=%s %s', filled, logContext.join(' '))
      maybeShowAd()
      maybeReleaseNextBatchForEmptyBoundary()
    }
  }, { threshold: 0 })
  loadObserver.observe(el)

  await waitForPreloadTurn(globalPreloadOrder)
  if (disposed)
    return

  debug('批次允许，开始预加载广告 %s', logContext.join(' '))
  filled = await displayAd(divId, unitPath, slotName)
  preloadFinished = true
  debug('预加载填充完成 filled=%s visible=%s %s', filled, visible, logContext.join(' '))
  maybeShowAd()
  maybeReleaseNextBatchForEmptyBoundary()
})

onUnmounted(() => {
  disposed = true
  loadObserver?.disconnect()
  reportObserver?.disconnect()
  destroyAd(divId)
})
</script>

<template>
  <div
    v-if="AD_ENABLED"
    :id="divId"
    ref="containerRef"
    class="chapter-ad-gpt"
  />
</template>

<style scoped>
.chapter-ad-gpt {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 1px;
  padding: 8px 0;
  visibility: hidden;
  max-height: 1px;
  overflow: hidden;
}
</style>
